import Debug from 'debug'
import { Result, ok, err } from '@xlearn/common';
import { Scorm2004Client } from './scorm2004-client'
import { ErrorHandlingQueue,
         ErrorHandlerResult,
         RetryStrategy, CommandError } from '@xlearn/async-toolbox'
import { RandomErrorGenerator } from '@xlearn/async-toolbox'
import { CMIElement, ScormError, CMIErrorCode } from '../api'
import { injectable } from 'inversify';
import { errorReporter } from '@/app/error-handling';
import { Observable, of } from 'rxjs';
import { ScormCommand, ScormResult } from './scorm-command'
import { ScormNotification } from './scorm-notification'


export const ErrorHandlingScorm2004ClientSymbol = Symbol.for('ErrorHandlingScorm2004Client')

const debug = Debug('error-handling-scorm2004-client')

const ENABLE_DEBUG_RANDOM_ERROR = false

export type Scorm2004CommandError = CommandError<ScormError>

// export function scormCommandOperator<T>(): OperatorFunction<ScormCommand, Observable<ScormCommand>> {
//   return (input$) => new Observable((observer) => {

//     input$.pipe(windowByComparator(setWindow))
//   })

//   // create window for 
//   function setWindow(a: ScormCommand, b: ScormCommand) {
//     return a.type === 'set-value' && b.type === 'set-value'
//   }
// }

// type NotificationOutput = {
//   type: 'communication-error'
//   display: 'show' | 'hide'
// } | {
//   type: 'communication-warning'
// } | {
//   type: 'communication-fatal'
// }

// export function scormErrorNotificationOperator<T>(): OperatorFunction<ScormNotification, Observable<ScormCommand>> {
//   return (input$) => new Observable((observer) => {

//     const $temporaryErrors = input$
//       .pipe(filter(x => [ 'executed', 'retry', 'executed-with-custom-result' ].indexOf(x.type) >= 0))

//     // const temporaryErrorDebouncer$ = input$
//     //   .pipe(
//     //     map(x => x.type === 'retry' ? 'error' : 'ok'),
//     //     errorDebounceOperator({ debounceMs: 1000 }))

//     const lastTemporaryError$ = input$.pipe(
//       filter(x => x.type === 'retry'))
     
//     input$.pipe(mergeMap(x => of(32)))
    
//     input$.pipe(share())
    
//     input$.pipe(groupBy(keySelector))
//   })

//   function keySelector(input: ScormNotification) {

//     switch (input.type) {
//       case 'executed':
//       case 'retry':
//       case 'executed-with-custom-result':
//         return 'communication'
//       case 'skipped':
//         return 'warning'
//       case 'fatal':
//         return 'fatal'
//     }
//   }  
// }

@injectable()
export class ErrorHandlingScorm2004Client {

  private errorHandlingQueue: ErrorHandlingQueue<ScormCommand, ScormResult, ScormError>

  private randomErrorGenerator?: RandomErrorGenerator<ScormError>
  private randomFatalErrorGenerator?: RandomErrorGenerator<ScormError>

  constructor(
    private client: Scorm2004Client,
    private retryStrategy: RetryStrategy) {

    debug('constructor')

    this.errorHandlingQueue = new ErrorHandlingQueue<ScormCommand, ScormResult, ScormError>(
      (command: ScormCommand) => this.executeCommand(command),
      (error: ScormError | Error, originalCommand: ScormCommand) => this.errorHandler(error, originalCommand),
      errorReporter,
      'scorm-queue'
    )

    if (ENABLE_DEBUG_RANDOM_ERROR) {
      this.enableRandomError()
      this.enableRandomFatalError()
    }
  }

  private enableRandomError() {
    const possibleErrors: ScormError[] = [
      new ScormError(CMIErrorCode.generalException, 'generalException'),
      new ScormError(CMIErrorCode.generalGetFailure, 'generalGetFailure'),
      new ScormError(CMIErrorCode.generalArgumentError, 'generalArgumentError')
    ]
    this.randomErrorGenerator = new RandomErrorGenerator<ScormError>(possibleErrors, {
      randomErrorProbability: 0.1,
      randomErrorLengthMs: { min: 500, max: 5000 }
    })
  }

  private enableRandomFatalError() {
    const possibleErrors: ScormError[] = [
      new ScormError(CMIErrorCode.contentInstanceTerminated, 'contentInstanceTerminated')
    ]
    this.randomFatalErrorGenerator = new RandomErrorGenerator<ScormError>(possibleErrors, {
      randomErrorProbability: 0.001,
      randomErrorLengthMs: { min: 500, max: 10000 }
    })
  }

  get notifications(): Observable<ScormNotification> {
    return this.errorHandlingQueue.notifications
  }

  public start() {
    debug('start')
    
    this.errorHandlingQueue.start(this.retryStrategy)
  }

  public async finish() {
    debug('finish')
    // we do not cancel, wait all of the requests to process
    await this.errorHandlingQueue.finish({ cancel: false });
  }

  public initialize() {
    debug('initialize')
    
    this.errorHandlingQueue.queue({
      type: 'initialize'
    })
  }

  public subscribeNotifications(
    handler: (notification: ScormNotification) => void) {

    return this.errorHandlingQueue.notifications.subscribe({
      next: (event) => {
        handler(event)
      }
    })
  }

  public async getValue(element: CMIElement) {
    debug('getValue', element)

    const result = await this.errorHandlingQueue.queueAndWait({
      type: 'get-value',
      element
    })

    return result

    // if (!result.success) {
    //   switch (result.error) {
    //     case 'skipped':
    //       return ''
    //   }
    // }

    // return result.value
  }

  public setValues(items: Array<[ CMIElement, string ]>) {
    for (let item of items) [
      this.setValue(item[0], item[1])
    ]
  }

  public setValue(element: CMIElement, value: string) {
    debug('setValue', element, value)

    this.errorHandlingQueue.queue({
      type: 'set-value',
      element,
      value
    })
  }

  public commit() {
    debug('commit')
    
    this.errorHandlingQueue.queue({
      type: 'commit'
    })
  }

  public terminate() {
    debug('terminate')

    this.errorHandlingQueue.queue({
      type: 'terminate'
    })
  }

  private async executeCommand (command: ScormCommand): Promise<Result<string, ScormError>> {
    debug('executeCommand', command)

    const randomError = this.generateRandomError()
    if (randomError !== false) {
      return randomError
    }

    switch (command.type) {
        case 'initialize':
        return this.client.initialize()
      case 'set-value':
        return this.client.setValue(command.element, command.value)
      case 'get-value':
        return this.client.getValue(command.element)
      case 'commit':
        return this.client.commit()
      case 'terminate':
        return this.client.terminate()
    }
  }

  private generateRandomError() {
    if (this.randomErrorGenerator) {
      const error = this.randomErrorGenerator.generate()
      if (error !== false) {
        debug('generated random error', error)
        return err(error)
      }
    }

    if (this.randomFatalErrorGenerator) {
      const error = this.randomFatalErrorGenerator.generate()
      if (error !== false) {
        debug('generated random fatal error', error)
        return err(error)
      }
    }

    return false
  }

  private errorHandler(error: ScormError | Error,
                       originalCommand: ScormCommand): ErrorHandlerResult<ScormCommand, string> {

    debug('errorHandler', error, originalCommand)

    if (error instanceof Error) {
      return {
        type: 'skip'
      }
    }

    switch (error.code) {
      case CMIErrorCode.noError:
        return { type: 'skip' }
      case CMIErrorCode.alreadyInitialized:
      case CMIErrorCode.terminationBeforeInitialization:
      case CMIErrorCode.terminationAfterTermination:
      case CMIErrorCode.commitAfterTermination:
      case CMIErrorCode.generalArgumentError:
      case CMIErrorCode.undefinedDataModelElement:
      case CMIErrorCode.unimplementedDataModelElement:
      case CMIErrorCode.dataModelElementIsReadOnly:
      case CMIErrorCode.dataModelElementTypeMismatch:
      case CMIErrorCode.dataModelElementValueOutOfRange:
      case CMIErrorCode.dataModelDependencyNotEstablished:
        return { type: 'skip' }
      case CMIErrorCode.generalException:
      case CMIErrorCode.generalInitializationFailure:
      case CMIErrorCode.generalTerminationFailure:
      case CMIErrorCode.generalGetFailure:
      case CMIErrorCode.generalSetFailure:
      case CMIErrorCode.generalCommitFailure:
        return { type: 'retry' }
      case CMIErrorCode.contentInstanceTerminated:
      case CMIErrorCode.retrieveDataAfterTermination:
      case CMIErrorCode.storeDataAfterTermination:
        return { type: 'fatal' }
      case CMIErrorCode.retrieveDataBeforeInitialization:
      case CMIErrorCode.storeDataBeforeInitialization:
      case CMIErrorCode.commitBeforeInitialization:
        return {
          type: 'prepend',
          commands: [ { type: 'initialize' } ]
        }
      case CMIErrorCode.dataModelElementValueNotInitialized:
      case CMIErrorCode.dataModelElementIsWriteOnly:
        return {
          type: 'custom-result',
          result: ''
        }
    }
  }

  
}
